import React, { useEffect } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { useRegistrationStore } from '../../../store/registrationStore';
import Required from '../Required';
import Title from '../Title';
import { AttendeeDetailsStepData } from './index';

const AttendeeDetailsStep = () => {
  const { userData, updateUserData, markStepValidated } =
    useRegistrationStore();
  const {
    register,
    control,
    formState: { errors, touchedFields },
    trigger
  } = useForm<AttendeeDetailsStepData>({
    defaultValues: userData.attendeeDetailsStep || {},
    mode: 'onChange'
  });

  const watchedFields = useWatch({ control });

  const validateFields = async () => {
    const isValid = await trigger();
    if (isValid) {
      const updatedFields = {
        ...userData.attendeeDetailsStep,
        ...watchedFields
      };

      updateUserData(
        'attendeeDetailsStep',
        updatedFields as AttendeeDetailsStepData
      );
      const { email, firstName, lastName, jobTitle, company, zipCode } =
        updatedFields;
      const allFieldsFilled = !!(
        email &&
        firstName &&
        lastName &&
        jobTitle &&
        company &&
        zipCode
      );
      markStepValidated(0, allFieldsFilled);
    } else {
      markStepValidated(0, false);
    }
  };

  useEffect(() => {
    validateFields();
  }, [watchedFields]);

  return (
    <form className="space-y-6 my-5 details-step">
      <Title title="Tell us a little about yourself" />
      <Required />
      <div className="grid gap-x-8 gap-y-4 md:grid-cols-2" style={{marginTop: 12}}>
        <label
          htmlFor="email"
          className="hidden mb-2 text-sm font-medium text-gray-900 dark:text-white"
        >
          Email
        </label>
        <div className="relative">
          <div className="absolute top-[14px] start-0 flex items-center ps-3.5 pointer-events-none">
            <svg
              className="w-4 h-4 text-gray-500 dark:text-gray-400"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
              viewBox="0 0 20 16"
            >
              <path d="m10.036 8.278 9.258-7.79A1.979 1.979 0 0 0 18 0H2A1.987 1.987 0 0 0 .641.541l9.395 7.737Z" />
              <path d="M11.241 9.817c-.36.275-.801.425-1.255.427-.428 0-.845-.138-1.187-.395L0 2.6V14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V2.5l-8.759 7.317Z" />
            </svg>
          </div>
          <input
            type="email"
            id="email"
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                message: 'Enter a valid email address'
              }
            })}
            className={`bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full max-w-[90%] ps-10 p-2.5  dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 ${
              errors.email && touchedFields.email
                ? 'border-red-500 bg-red-50'
                : 'border-gray-300'
            }`}
            placeholder="Email"
          />
          {errors.email && touchedFields.email && (
            <span className="text-red-500 text-sm">
              {errors.email?.message}
            </span>
          )}
        </div>

        <div>
          <label
            htmlFor="firstName"
            className="hidden mb-2 text-sm font-medium text-gray-900 dark:text-white"
          >
            First Name
          </label>
          <input
            type="text"
            id="firstName"
            {...register('firstName', { required: 'First name is required' })}
            className={`bg-gray-50 border ${
              errors.firstName && touchedFields.firstName
                ? 'border-red-500 bg-red-50'
                : 'border-gray-300'
            } text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full max-w-[100%] p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500`}
            placeholder="* First Name"
          />
          {errors.firstName && touchedFields.firstName && (
            <span className="text-red-500 text-sm">
              {errors.firstName?.message}
            </span>
          )}
        </div>

        <div>
          <label
            htmlFor="lastName"
            className="hidden mb-2 text-sm font-medium text-gray-900 dark:text-white"
          >
            Last Name
          </label>
          <input
            type="text"
            id="lastName"
            {...register('lastName', { required: 'Last name is required' })}
            className={`bg-gray-50 border ${
              errors.lastName && touchedFields.lastName
                ? 'border-red-500 bg-red-50'
                : 'border-gray-300'
            } text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full max-w-[100%] p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500`}
            placeholder="* Last Name"
          />
          {errors.lastName && touchedFields.lastName && (
            <span className="text-red-500 text-sm">
              {errors.lastName?.message}
            </span>
          )}
        </div>

        <div>
          <label
            htmlFor="company"
            className="hidden mb-2 text-sm font-medium text-gray-900 dark:text-white"
          >
            Company
          </label>
          <input
            type="text"
            id="company"
            {...register('company', { required: 'Company is required' })}
            className={`bg-gray-50 border ${
              errors.company && touchedFields.company
                ? 'border-red-500 bg-red-50'
                : 'border-gray-300'
            } text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full max-w-[100%] p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500`}
            placeholder="* Company"
          />
          {errors.company && touchedFields.company && (
            <span className="text-red-500 text-sm">
              {errors.company?.message}
            </span>
          )}
        </div>

        <div>
          <label
            htmlFor="jobTitle"
            className="hidden mb-2 text-sm font-medium text-gray-900 dark:text-white"
          >
            Job Title
          </label>
          <input
            type="text"
            id="jobTitle"
            {...register('jobTitle', { required: 'Job title is required' })}
            className={`bg-gray-50 border ${
              errors.jobTitle && touchedFields.jobTitle
                ? 'border-red-500 bg-red-50'
                : 'border-gray-300'
            } text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full max-w-[100%] p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500`}
            placeholder="* Job Title"
          />
          {errors.jobTitle && touchedFields.jobTitle && (
            <span className="text-red-500 text-sm">
              {errors.jobTitle?.message}
            </span>
          )}
        </div>

        <div className='phone-input-container'>
          <label htmlFor="phoneNumber" className="hidden mb-2 text-sm font-medium text-gray-900 dark:text-white">
            Phone Number
          </label>
          <Controller
            name="phoneNumber"
            control={control}
            rules={{
              validate: (value) => typeof value === 'string' && isValidPhoneNumber(value)
            }}
            render={({ field }) => (
              <PhoneInput
                value={field.value}
                defaultCountry="US"
                onChange={(value) => field.onChange(value || '')}
                className={`bg-gray-50 border ${
                  touchedFields.phoneNumber && errors.phoneNumber
                    ? 'border-red-500 bg-red-50'
                    : 'border-gray-300'
                } text-gray-900 text-sm rounded-lg block w-full max-w-[100%] dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white`}
                placeholder="Enter your phone number"
              />
            )}
          />
          {errors.phoneNumber && touchedFields.phoneNumber && (
            <span className="text-red-500 text-sm">
              {errors.phoneNumber?.message}
            </span>
          )}
        </div>

        <div>
          <label
            htmlFor="zipCode"
            className="hidden mb-2 text-sm font-medium text-gray-900 dark:text-white"
          >
            Zip Code
          </label>
          <input
            type="text"
            id="zipCode"
            {...register('zipCode', {
              required: 'Zip code is required',
              pattern: {
                value: /^\d{5}(?:[-\s]\d{4})?$/,
                message: 'Enter a valid 5-digit zip code'
              }
            })}
            className={`bg-gray-50 border ${
              errors.zipCode && touchedFields.zipCode
                ? 'border-red-500 bg-red-50'
                : 'border-gray-300'
            } text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full max-w-[100%] p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 border-[1px]`}
            placeholder="*Zip Code"
          />
          {errors.zipCode && touchedFields.zipCode && (
            <span className="text-red-500 text-sm">
              {errors.zipCode?.message}
            </span>
          )}
        </div>
      </div>
    </form>
  );
};

export default AttendeeDetailsStep;
